import { useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, FileJson, Info, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  blockersByRow,
  buildImportChoices,
  notesByRow,
  readBundleFile,
  setImportFormName,
  summarizeImport,
  type ImportDraft,
} from '@/lib/procedureTemplateImport'
import type {
  ProcedureTemplateImportPlan,
} from '@/types/procedureTemplateTransfer'
import type { ProcedureTemplateDetail } from '@/types/cgt'

interface ImportProcedureTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImported: (created: ProcedureTemplateDetail) => void
}

/**
 * Import a procedure template from a bundle exported elsewhere (z8nrz7dz4g).
 *
 * Three steps, and the middle one is the point: pick a file, PREVIEW what it
 * would do here, then commit. The preview is not a formality — a bundle from
 * another environment can name materials or equipment this one has never had,
 * and the only place that can be discovered is against this tenant's data.
 *
 * Every decision is the server's (`transfer/resolve-import.ts`); this dialog
 * renders the plan it returns and collects the names and opt-ins the user can
 * change. It deliberately does NOT pre-judge the bundle: a client-side notion
 * of "valid" would be a second definition of the one the server must enforce.
 */
export function ImportProcedureTemplateDialog({
  open,
  onOpenChange,
  onImported,
}: ImportProcedureTemplateDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null)

  const [draft, setDraft] = useState<ImportDraft | null>(null)
  const [plan, setPlan] = useState<ProcedureTemplateImportPlan | null>(null)
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signing, setSigning] = useState(false)

  function reset() {
    setDraft(null)
    setPlan(null)
    setError(null)
    setBusy(null)
    setSigning(false)
    if (fileInput.current) fileInput.current.value = ''
  }

  function close(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  async function handleFile(file: File) {
    setError(null)
    setPlan(null)
    const result = readBundleFile(file.name, await file.text())
    if (!result.ok) {
      setDraft(null)
      setError(result.error)
      return
    }
    setDraft(result.draft)
    await runPreview(result.draft)
  }

  async function runPreview(next: ImportDraft) {
    setBusy('preview')
    setError(null)
    try {
      const { plan: fresh } = await procedureTemplatesApi.previewImport(
        next.bundle,
        buildImportChoices(next),
      )
      setPlan(fresh)
    } catch (err) {
      setPlan(null)
      setError(apiErrorMessage(err, 'That bundle could not be read.'))
    } finally {
      setBusy(null)
    }
  }

  async function commit(signingToken?: string) {
    if (!draft) return
    setBusy('commit')
    setError(null)
    try {
      const choices = buildImportChoices(draft)
      const created = signingToken
        ? await procedureTemplatesApi.commitImportAsVersion(draft.bundle, choices, signingToken)
        : await procedureTemplatesApi.commitImport(draft.bundle, choices)
      onImported(created)
      close(false)
    } catch (err) {
      // A commit re-plans server-side, so this is where a material deleted
      // since the preview surfaces. Re-run the preview so the dialog shows
      // WHICH one rather than just the message.
      setError(apiErrorMessage(err, 'The import could not be completed.'))
      setSigning(false)
      void runPreview(draft)
    } finally {
      setBusy(null)
    }
  }

  const summary = plan ? summarizeImport(plan) : null
  const rowBlockers = plan ? blockersByRow(plan) : {}
  const rowNotes = plan ? notesByRow(plan) : {}
  const templateBlockers = rowBlockers.template ?? []
  const templateNotes = rowNotes.template ?? []
  const canCommit = Boolean(plan?.canCommit) && busy === null

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Import procedure template</DialogTitle>
            <DialogDescription>
              Load a bundle exported from another environment. Nothing is written until you
              commit, and the import is refused outright if anything it needs is missing here.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='flex items-center gap-3'>
              <input
                ref={fileInput}
                type='file'
                accept='.json,application/json'
                className='hidden'
                onChange={e => {
                  const file = e.target.files?.[0]
                  if (file) void handleFile(file)
                }}
              />
              <Button
                variant='outline'
                onClick={() => fileInput.current?.click()}
                disabled={busy !== null}
              >
                <Upload className='mr-2 h-4 w-4' />
                {draft ? 'Choose a different file' : 'Choose bundle file'}
              </Button>
              {draft && (
                <span className='flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground'>
                  <FileJson className='h-4 w-4 shrink-0' />
                  <span className='truncate'>{draft.fileName}</span>
                </span>
              )}
            </div>

            {draft?.bundle.source && (
              <p className='text-xs text-muted-foreground'>
                Exported{' '}
                {draft.bundle.exportedAt
                  ? new Date(draft.bundle.exportedAt).toLocaleString()
                  : 'at an unknown time'}
                {draft.bundle.source.environment ? ` from ${draft.bundle.source.environment}` : ''}
                {draft.bundle.source.tenantName ? ` · ${draft.bundle.source.tenantName}` : ''}
              </p>
            )}

            {error && (
              <p className='flex items-start gap-1.5 text-sm text-destructive'>
                <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
                <span>{error}</span>
              </p>
            )}

            {busy === 'preview' && (
              <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Checking this bundle against your environment…
              </p>
            )}

            {plan && summary && (
              <>
                <div
                  className={
                    summary.blockerCount > 0
                      ? 'flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive'
                      : 'flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm'
                  }
                >
                  {summary.blockerCount > 0 ? (
                    <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
                  ) : (
                    <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' />
                  )}
                  <span>{summary.headline}</span>
                </div>

                <div className='space-y-1.5'>
                  <Label htmlFor='import-template-name'>Template name</Label>
                  <Input
                    id='import-template-name'
                    value={draft?.templateName ?? ''}
                    disabled={plan.template.action === 'new-version'}
                    onChange={e => {
                      if (!draft) return
                      setDraft({ ...draft, templateName: e.target.value })
                    }}
                    onBlur={() => draft && void runPreview(draft)}
                  />
                  {plan.template.action === 'new-version' && (
                    <p className='text-xs text-muted-foreground'>
                      A new version keeps the family&rsquo;s name.
                    </p>
                  )}
                  {templateBlockers.map(message => (
                    <p key={message} className='text-xs text-destructive'>
                      {message}
                    </p>
                  ))}
                  {templateNotes.map(message => (
                    <p key={message} className='flex items-start gap-1 text-xs text-muted-foreground'>
                      <Info className='mt-0.5 h-3 w-3 shrink-0' />
                      <span>{message}</span>
                    </p>
                  ))}
                </div>

                {plan.forms.length > 0 && (
                  <div className='space-y-2'>
                    <Label>Forms</Label>
                    {plan.forms.map(form => {
                      const blockers = rowBlockers[form.handle] ?? []
                      const notes = rowNotes[form.handle] ?? []
                      return (
                        <div key={form.handle} className='space-y-1'>
                          <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-2'>
                            <span className='truncate text-sm text-muted-foreground'>
                              {form.sourceName}
                            </span>
                            <span
                              className={
                                form.action === 'reuse'
                                  ? 'rounded bg-muted px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-muted-foreground'
                                  : 'rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-emerald-700'
                              }
                            >
                              {form.action === 'reuse' ? 'reused' : 'new'}
                            </span>
                            <Input
                              aria-label={`Name for ${form.sourceName}`}
                              value={
                                form.action === 'reuse'
                                  ? form.name
                                  : (draft?.formNames[form.handle] ?? form.name)
                              }
                              disabled={form.action === 'reuse'}
                              onChange={e => {
                                if (!draft) return
                                setDraft(setImportFormName(draft, form.handle, e.target.value))
                              }}
                              onBlur={() => draft && void runPreview(draft)}
                            />
                          </div>
                          {blockers.map(message => (
                            <p key={message} className='text-xs text-destructive'>
                              {message}
                            </p>
                          ))}
                          {notes.map(message => (
                            <p key={message} className='text-xs text-muted-foreground'>
                              {message}
                            </p>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                )}

                <div className='space-y-2 border-t pt-3'>
                  {plan.template.existingTemplateId !== undefined || draft?.asNewVersion ? (
                    <label className='flex items-start gap-2 text-sm'>
                      <Checkbox
                        checked={draft?.asNewVersion ?? false}
                        onCheckedChange={checked => {
                          if (!draft) return
                          const next = { ...draft, asNewVersion: checked === true }
                          setDraft(next)
                          void runPreview(next)
                        }}
                      />
                      <span>
                        Import as a new VERSION of the template family this bundle names, rather
                        than a separate template.
                        <span className='block text-xs text-muted-foreground'>
                          Requires your password, because it changes a family that may already be
                          in use.
                        </span>
                      </span>
                    </label>
                  ) : null}

                  {draft?.bundle.template?.productCode ? (
                    <label className='flex items-start gap-2 text-sm'>
                      <Checkbox
                        checked={draft.applyProductCode}
                        onCheckedChange={checked => {
                          const next = { ...draft, applyProductCode: checked === true }
                          setDraft(next)
                          void runPreview(next)
                        }}
                      />
                      <span>
                        Apply the product code{' '}
                        <code className='text-xs'>{draft.bundle.template.productCode}</code>
                        <span className='block text-xs text-muted-foreground'>
                          Inbound patient events resolve a template by this code, so claiming it
                          here changes routing.
                        </span>
                      </span>
                    </label>
                  ) : null}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => close(false)} disabled={busy === 'commit'}>
              Cancel
            </Button>
            <Button
              disabled={!canCommit}
              onClick={() => {
                if (draft?.asNewVersion) setSigning(true)
                else void commit()
              }}
            >
              {busy === 'commit' && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {draft?.asNewVersion ? 'Sign & import version' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={signing}
        onOpenChange={setSigning}
        title='Import as a new version'
        description='This mints a new version of a template family that may already be in use.'
        confirmLabel='Sign & Import'
        busy={busy === 'commit'}
        onConfirm={token => void commit(token)}
      />
    </>
  )
}
